// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset } from '../types';
import { num, rate, pct, esc, COL } from '../format';
import { gloss } from '../glossary';
import { tipAttr, niceMax, linePoints } from '../charts';
import { openState } from '../main';

export function renderRemand(root: HTMLElement, data: Dataset): void {
  const { national, remand, meta } = data;
  const c = meta.counts;
  const bandTotal = remand.bands.reduce((a, b) => a + (b.national ?? 0), 0);
  const longWait = remand.bands.filter((b) => /year/i.test(b.label)).reduce((a, b) => a + (b.national ?? 0), 0);

  root.innerHTML = `
    <div class="view-head">
      <h2>Held, but not sentenced</h2>
      <p>A ${gloss('remand', 'remand')} prisoner is in custody awaiting trial or sentencing — not serving a sentence, and legally presumed innocent. This group has driven most of the growth in Australia's prisons: it is now ${pct(c.remandShare)} of all prisoners, up from ${pct(c.remandShare2016)} in ${meta.firstYear}.</p>
    </div>

    <div class="stat-row">
      <div class="stat hl"><div class="stat-label">On remand now</div><div class="stat-value">${num(c.unsentenced)}</div><div class="stat-note">${pct(c.remandShare)} of all prisoners</div></div>
      <div class="stat"><div class="stat-label">Sentenced</div><div class="stat-value">${num(c.sentenced)}</div><div class="stat-note">${pct(1 - c.remandShare)} of prisoners</div></div>
      <div class="stat"><div class="stat-label">Median wait</div><div class="stat-value">${rate(remand.stateStat.AUS?.median)} mo</div><div class="stat-note">half wait longer</div></div>
      <div class="stat"><div class="stat-label">Waiting a year or more</div><div class="stat-value">${bandTotal ? pct(longWait / bandTotal) : '—'}</div><div class="stat-note">${num(longWait)} people</div></div>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>The remand share, ${national.years[0]}–${national.years[national.years.length - 1]}</h3><p>Unsentenced prisoners as a share of all prisoners.</p></div>
      <div class="chart-wrap" id="rem-line"></div>
    </div>

    <div class="two-col" style="margin-top:var(--space-lg)">
      <div class="panel">
        <div class="panel-head"><h3>How long people wait</h3><p>Time already spent on remand, all unsentenced prisoners, ${meta.latestYear}.</p></div>
        <div class="chart-wrap" id="rem-hist"></div>
      </div>
      <div class="panel">
        <div class="panel-head"><h3>Remand by state</h3><p>Share of each state's prisoners who are unsentenced. Click a state for detail.</p></div>
        <div id="rem-states"></div>
      </div>
    </div>
  `;

  drawShareLine(root.querySelector('#rem-line') as HTMLElement, data);
  drawHist(root.querySelector('#rem-hist') as HTMLElement, data);
  drawStates(root.querySelector('#rem-states') as HTMLElement, data);
}

function drawShareLine(el: HTMLElement, data: Dataset): void {
  const { national } = data;
  const share = national.total.map((t, i) => (t ? ((national.unsentenced[i] ?? 0) / t) * 100 : null));
  const W = 900, H = 240, m = { t: 14, r: 16, b: 28, l: 40 };
  const iw = W - m.l - m.r, ih = H - m.t - m.b;
  const maxV = niceMax(Math.max(...share.filter((v): v is number => v != null)));
  const x = (i: number) => m.l + (i / (national.years.length - 1)) * iw;
  const y = (v: number) => m.t + ih - (v / maxV) * ih;
  const ticks = 4;
  const gridY = Array.from({ length: ticks + 1 }, (_, i) => (maxV / ticks) * i);
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Remand share over time">
    ${gridY.map((g) => `<line class="grid-line" x1="${m.l}" y1="${y(g).toFixed(1)}" x2="${W - m.r}" y2="${y(g).toFixed(1)}"/><text class="axis-text" x="${m.l - 8}" y="${y(g) + 3}" text-anchor="end">${g.toFixed(0)}%</text>`).join('')}
    ${national.years.map((yr, i) => `<text class="axis-text" x="${x(i).toFixed(1)}" y="${H - 8}" text-anchor="middle">${String(yr).slice(2)}</text>`).join('')}
    <polyline class="series-line" points="${linePoints(share, x, y)}" stroke="${COL.remand}"/>
    ${share.map((v, i) => (v == null ? '' : `<circle class="dot" cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="6" fill="transparent" ${tipAttr(`${national.years[i]}: ${v.toFixed(1)}% on remand (${num(national.unsentenced[i])})`)} aria-label="${national.years[i]}: ${v.toFixed(1)}%"></circle><circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="2.6" fill="${COL.remand}"/>`)).join('')}
  </svg>`;
}

function drawHist(el: HTMLElement, data: Dataset): void {
  const bands = data.remand.bands;
  const W = 440, H = 250, m = { t: 12, r: 12, b: 54, l: 40 };
  const iw = W - m.l - m.r, ih = H - m.t - m.b;
  const maxV = niceMax(Math.max(...bands.map((b) => b.national ?? 0)));
  const bw = iw / bands.length;
  const y = (v: number) => m.t + ih - (v / maxV) * ih;
  const ticks = 4;
  const gridY = Array.from({ length: ticks + 1 }, (_, i) => (maxV / ticks) * i);
  const shortLbl = (l: string) => l.replace(' months', 'mo').replace('Under ', '<').replace(' to under ', '–').replace(' month', 'mo').replace('1 month', '1mo').replace('years & over', 'yr+').replace(' years', 'yr').replace(' year', 'yr');
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Time on remand distribution">
    ${gridY.map((g) => `<line class="grid-line" x1="${m.l}" y1="${y(g).toFixed(1)}" x2="${W - m.r}" y2="${y(g).toFixed(1)}"/><text class="axis-text" x="${m.l - 6}" y="${y(g) + 3}" text-anchor="end">${num(g)}</text>`).join('')}
    ${bands.map((b, i) => {
      const v = b.national ?? 0;
      const x = m.l + i * bw + 3;
      const h = ih - (y(v) - m.t);
      return `<rect class="bar" x="${x.toFixed(1)}" y="${y(v).toFixed(1)}" width="${(bw - 6).toFixed(1)}" height="${Math.max(0, h).toFixed(1)}" fill="${COL.remand}" rx="2" ${tipAttr(`${b.label}\n${num(v)} prisoners`)} aria-label="${esc(b.label)}: ${num(v)}"></rect>
        <text class="axis-text" x="${(x + (bw - 6) / 2).toFixed(1)}" y="${H - m.b + 14}" text-anchor="end" transform="rotate(-40 ${(x + (bw - 6) / 2).toFixed(1)} ${H - m.b + 14})">${esc(shortLbl(b.label))}</text>`;
    }).join('')}
  </svg>`;
}

function drawStates(el: HTMLElement, data: Dataset): void {
  const rows = data.states
    .filter((s) => s.remandShareNow != null)
    .slice()
    .sort((a, b) => (b.remandShareNow as number) - (a.remandShareNow as number));
  const max = Math.max(...rows.map((s) => s.remandShareNow as number), 0.01);
  el.className = 'barlist';
  el.innerHTML = rows.map((s) => `
    <div class="barrow" data-state="${s.code}" role="button" tabindex="0" aria-label="${esc(s.name)}: ${pct(s.remandShareNow)} on remand" ${tipAttr(`${s.name}\n${pct(s.remandShareNow)} on remand\nMedian wait ${s.remandMedian == null ? '—' : rate(s.remandMedian) + ' months'}`)}>
      <span class="lbl">${esc(s.name)}</span>
      <span class="track"><span style="width:${((s.remandShareNow as number) / max * 100).toFixed(1)}%;background:${COL.remand}"></span></span>
      <span class="val">${pct(s.remandShareNow)}</span>
    </div>`).join('');
  const go = (c: string) => openState(c);
  el.querySelectorAll<HTMLElement>('[data-state]').forEach((r) => {
    r.addEventListener('click', () => go(r.dataset.state!));
    r.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') { e.preventDefault(); go(r.dataset.state!); } });
  });
}
